// Composable for doughnut chart using Chart.js.
// Renders a donut with center label showing total duration.

import { watch, onUnmounted, shallowRef } from 'vue'
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js'

Chart.register(DoughnutController, ArcElement, Tooltip, Legend)

const centerTextPlugin = {
  id: 'centerText',
  afterDraw(chart) {
    const { ctx, width, height } = chart
    const meta = chart.options.plugins.centerText
    if (!meta?.text) return
    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const cx = width / 2
    const cy = height / 2
    ctx.font = '600 20px system-ui, -apple-system, sans-serif'
    ctx.fillStyle = '#333'
    ctx.fillText(meta.text, cx, cy - 8)
    ctx.font = '400 10px system-ui, -apple-system, sans-serif'
    ctx.fillStyle = '#999'
    ctx.fillText('TOTAL', cx, cy + 12)
    ctx.restore()
  },
}

Chart.register(centerTextPlugin)

function formatHm(seconds) {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${h}h ${m}m`
}

export function useDoughnutChart(canvasRef, itemsRef) {
  const chart = shallowRef(null)

  function buildChart() {
    const canvas = canvasRef.value
    const items = itemsRef.value
    if (!canvas || !items?.length) return

    if (chart.value) {
      chart.value.destroy()
      chart.value = null
    }

    const total = items.reduce((sum, it) => sum + it.seconds, 0)

    chart.value = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: items.map(it => it.name),
        datasets: [{
          data: items.map(it => it.seconds),
          backgroundColor: items.map(it => it.color),
          borderWidth: 2,
          borderColor: '#fff',
          hoverOffset: 4,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutout: '62%',
        plugins: {
          legend: { display: false },
          centerText: { text: formatHm(total) },
          tooltip: {
            displayColors: false,
            titleFont: { size: 0 },
            bodyFont: { size: 11 },
            callbacks: {
              title: () => '',
              label: (ctx) => {
                const secs = ctx.parsed
                const pct = total > 0 ? Math.round(secs / total * 100) : 0
                return `${ctx.label} · ${formatHm(secs)} · ${pct}%`
              },
            },
          },
        },
      },
    })
  }

  watch(itemsRef, buildChart, { deep: true, flush: 'post' })

  onUnmounted(() => {
    if (chart.value) {
      chart.value.destroy()
      chart.value = null
    }
  })

  return { chart, rebuild: buildChart }
}
